<template>
  <!--
    Issue #48 — GlobalSearch.vue
    Tarea 17.1: barra de búsqueda, Ctrl+K, modal expandida, sugerencias
    Tarea 17.2: búsqueda por voz (Web Speech API)
    Tarea 17.3: indexación de documentos, informes, indicadores y ayuda
  -->
  <Teleport to="body">
    <Transition name="gs-fade">
      <div
        v-if="open"
        class="gs-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gs-title"
        @click.self="close"
      >
        <div class="gs-modal" ref="modalRef" @keydown.esc="close">
          <!-- Título visualmente oculto -->
          <span id="gs-title" class="sr-only">Búsqueda global</span>

          <!-- ── Barra de búsqueda (tarea 17.1.1) ── -->
          <div class="gs-input-row">
            <i class="pi pi-search gs-search-icon" aria-hidden="true" />
            <input
              ref="inputRef"
              v-model="query"
              type="search"
              class="gs-input"
              placeholder="Buscar documentos, informes, indicadores…"
              autocomplete="off"
              aria-label="Búsqueda global"
              aria-controls="gs-results"
              :aria-expanded="results.length > 0"
              @input="search"
              @keydown.down.prevent="focusResult(0)"
              @keydown.enter="activateFirst"
            />

            <!-- Indicador de voz (tarea 17.2.3) -->
            <button
              v-if="voiceSupported"
              class="gs-voice-btn"
              :class="{ 'gs-voice-btn--active': listening }"
              type="button"
              :aria-label="listening ? 'Detener grabación de voz' : 'Búsqueda por voz'"
              @click="toggleVoice"
            >
              <i :class="listening ? 'pi pi-stop-circle' : 'pi pi-microphone'" aria-hidden="true" />
              <span v-if="listening" class="gs-voice-pulse" aria-hidden="true" />
            </button>

            <!-- Tecla de cierre -->
            <kbd class="gs-esc-hint" aria-hidden="true">Esc</kbd>
          </div>

          <!-- Transcripción en tiempo real (tarea 17.2.4) -->
          <div v-if="listening" class="gs-transcript" aria-live="polite" aria-atomic="true">
            <i class="pi pi-microphone" aria-hidden="true" />
            {{ transcript || 'Escuchando…' }}
          </div>

          <!-- ── Resultados (tarea 17.1.4) ── -->
          <div
            id="gs-results"
            class="gs-results"
            role="listbox"
            aria-label="Resultados de búsqueda"
          >
            <!-- Sin query: sugerencias de acceso rápido -->
            <template v-if="!query">
              <p class="gs-hint">Escribe para buscar o usa las sugerencias rápidas:</p>
              <div class="gs-quick">
                <button
                  v-for="q in QUICK"
                  :key="q.label"
                  class="gs-quick-chip"
                  type="button"
                  @click="query = q.label; search()"
                >
                  <i :class="q.icon" aria-hidden="true" /> {{ q.label }}
                </button>
              </div>

              <!-- Atajos de teclado -->
              <div class="gs-shortcuts">
                <p class="gs-shortcuts-title">Atajos de teclado:</p>
                <div class="gs-shortcut-list">
                  <span class="gs-shortcut"><kbd>Ctrl</kbd><kbd>K</kbd> Abrir búsqueda</span>
                  <span class="gs-shortcut"><kbd>↑</kbd><kbd>↓</kbd> Navegar</span>
                  <span class="gs-shortcut"><kbd>Enter</kbd> Seleccionar</span>
                  <span class="gs-shortcut"><kbd>Esc</kbd> Cerrar</span>
                </div>
              </div>
            </template>

            <!-- Con query: resultados categorizados (tarea 17.3) -->
            <template v-else>
              <div v-if="results.length === 0" class="gs-no-results" role="status">
                <i class="pi pi-search-minus" aria-hidden="true" />
                <p>Sin resultados para <strong>{{ query }}</strong></p>
              </div>

              <template v-for="cat in CATEGORIES" :key="cat.key">
                <div
                  v-if="resultsByCategory(cat.key).length"
                  class="gs-category"
                  role="group"
                  :aria-label="`Categoría: ${cat.label}`"
                >
                  <p class="gs-cat-label">
                    <i :class="cat.icon" aria-hidden="true" /> {{ cat.label }}
                  </p>
                  <button
                    v-for="(item, idx) in resultsByCategory(cat.key)"
                    :key="item.id"
                    :ref="(el) => setResultRef(el as HTMLElement, cat.key, idx)"
                    role="option"
                    :aria-selected="false"
                    class="gs-result-item"
                    type="button"
                    @click="activate(item)"
                    @keydown="handleResultKey($event, item)"
                  >
                    <i :class="item.icon" class="gs-result-icon" aria-hidden="true" />
                    <div class="gs-result-text">
                      <span class="gs-result-title" v-html="highlight(item.title)" />
                      <span class="gs-result-sub">{{ item.subtitle }}</span>
                    </div>
                    <span v-if="item.badge" class="gs-result-badge" :class="`badge--${item.badgeType}`">
                      {{ item.badge }}
                    </span>
                  </button>
                </div>
              </template>
            </template>
          </div>

          <!-- Footer -->
          <div class="gs-footer" aria-hidden="true">
            <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
            <span><kbd>Enter</kbd> abrir</span>
            <span><kbd>Esc</kbd> cerrar</span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { documentsService } from '@/services/documents'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()

// ── Visibilidad ────────────────────────────────────────────────────────────────
const open = ref(false)
const modalRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

function openSearch() {
  open.value = true
  nextTick(() => inputRef.value?.focus())
}
function close() {
  open.value = false
  query.value = ''
  results.value = []
  stopVoice()
}

// Exponer para integración externa (Ctrl+K desde useAccessibility)
defineExpose({ openSearch })

// ── Búsqueda ──────────────────────────────────────────────────────────────────
const query = ref('')
const results = ref<SearchItem[]>([])
let debounceTimer: ReturnType<typeof setTimeout> | null = null

interface SearchItem {
  id: string
  category: 'documents' | 'reports' | 'indicators' | 'help'
  title: string
  subtitle: string
  icon: string
  badge?: string
  badgeType?: string
  action?: () => void
}

// Índice estático de informes e indicadores (tarea 17.3.2 y 17.3.3)
const REPORT_INDEX: SearchItem[] = [
  { id: 'r1', category: 'reports', title: 'Balance General', subtitle: 'Informe obligatorio NIC 1', icon: 'pi pi-file', badge: 'PDF', badgeType: 'pdf' },
  { id: 'r2', category: 'reports', title: 'Estado de Resultados', subtitle: 'Informe de pérdidas y ganancias', icon: 'pi pi-file', badge: 'PDF', badgeType: 'pdf' },
  { id: 'r3', category: 'reports', title: 'Flujo de Caja', subtitle: 'Análisis de efectivo', icon: 'pi pi-chart-bar', badge: 'Excel', badgeType: 'excel' },
  { id: 'r4', category: 'reports', title: 'Capital de Trabajo', subtitle: 'Análisis de liquidez operacional', icon: 'pi pi-chart-pie', badge: 'PDF', badgeType: 'pdf' },
  { id: 'r5', category: 'reports', title: 'Envejecimiento de Cartera', subtitle: 'Análisis de cuentas por cobrar', icon: 'pi pi-users', badge: 'Excel', badgeType: 'excel' },
]

const INDICATOR_INDEX: SearchItem[] = [
  { id: 'i1', category: 'indicators', title: 'Razón Corriente', subtitle: 'Activos corrientes / Pasivos corrientes', icon: 'pi pi-calculator' },
  { id: 'i2', category: 'indicators', title: 'Prueba Ácida', subtitle: 'Liquidez inmediata sin inventarios', icon: 'pi pi-calculator' },
  { id: 'i3', category: 'indicators', title: 'ROE', subtitle: 'Rentabilidad sobre patrimonio', icon: 'pi pi-chart-line' },
  { id: 'i4', category: 'indicators', title: 'ROA', subtitle: 'Rentabilidad sobre activos', icon: 'pi pi-chart-line' },
  { id: 'i5', category: 'indicators', title: 'EBITDA', subtitle: 'Utilidad operativa ajustada', icon: 'pi pi-dollar' },
  { id: 'i6', category: 'indicators', title: 'Margen Neto', subtitle: 'Utilidad neta / Ingresos totales', icon: 'pi pi-percentage' },
  { id: 'i7', category: 'indicators', title: 'Endeudamiento Total', subtitle: 'Pasivos / Activos totales', icon: 'pi pi-credit-card' },
]

const HELP_INDEX: SearchItem[] = [
  { id: 'h1', category: 'help', title: 'Cómo cargar un estado financiero', subtitle: 'Guía paso a paso para subir documentos', icon: 'pi pi-upload', action: () => router.push('/financial') },
  { id: 'h2', category: 'help', title: 'Interpretar el dashboard', subtitle: 'Cómo leer los indicadores del tablero', icon: 'pi pi-question-circle' },
  { id: 'h3', category: 'help', title: 'Exportar informes', subtitle: 'Descarga en PDF o Excel', icon: 'pi pi-download' },
  { id: 'h4', category: 'help', title: 'Comparación temporal', subtitle: 'Comparar dos períodos financieros', icon: 'pi pi-calendar' },
]

const CATEGORIES = [
  { key: 'documents',  label: 'Documentos',  icon: 'pi pi-folder-open' },
  { key: 'reports',    label: 'Informes',    icon: 'pi pi-file-pdf' },
  { key: 'indicators', label: 'Indicadores', icon: 'pi pi-chart-bar' },
  { key: 'help',       label: 'Ayuda',       icon: 'pi pi-info-circle' },
] as const

const QUICK = [
  { label: 'balance', icon: 'pi pi-file' },
  { label: 'flujo de caja', icon: 'pi pi-chart-bar' },
  { label: 'razón corriente', icon: 'pi pi-calculator' },
  { label: 'exportar PDF', icon: 'pi pi-download' },
]

// Cache de documentos para búsqueda (tarea 17.3.1)
const authStore = useAuthStore()
let docCache: SearchItem[] = []

interface SearchDocument {
  id: number
  filename?: string
  period_label?: string
  created_at?: string
}

function isSearchDocument(value: unknown): value is SearchDocument {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return typeof candidate.id === 'number'
}

async function loadDocCache() {
  if (!authStore.isAuthenticated) return
  try {
    const docs = await documentsService.list()
    const safeDocs = Array.isArray(docs) ? docs.filter(isSearchDocument) : []
    docCache = safeDocs.map((d) => ({
      id: `doc-${d.id}`,
      category: 'documents' as const,
      title: d.filename ?? d.period_label ?? `Documento ${d.id}`,
      subtitle: d.period_label ?? d.created_at?.slice(0, 10) ?? '',
      icon: 'pi pi-file-excel',
      action: () => router.push(`/financial/dashboard?document_id=${d.id}`),
    }))
  } catch { /* silently fail */ }
}
loadDocCache()

function search() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    const q = query.value.trim().toLowerCase()
    if (!q) { results.value = []; return }

    const all = [...docCache, ...REPORT_INDEX, ...INDICATOR_INDEX, ...HELP_INDEX]
    results.value = all.filter(item =>
      item.title.toLowerCase().includes(q) ||
      item.subtitle.toLowerCase().includes(q)
    ).slice(0, 20)
  }, 150)
}

function resultsByCategory(cat: string) {
  return results.value.filter(r => r.category === cat)
}

function highlight(text: string) {
  if (!query.value) return text
  const q = query.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return text.replace(new RegExp(`(${q})`, 'gi'), '<mark>$1</mark>')
}

function activate(item: SearchItem) {
  if (item.action) item.action()
  else if (item.category === 'documents') router.push('/financial/dashboard')
  close()
}

function activateFirst() {
  if (results.value.length > 0) activate(results.value[0])
}

// ── Navegación por teclado entre resultados ────────────────────────────────────
const resultRefs: Record<string, HTMLElement[]> = {}

function setResultRef(el: HTMLElement | null, cat: string, idx: number) {
  if (!resultRefs[cat]) resultRefs[cat] = []
  if (el) resultRefs[cat][idx] = el
}

function focusResult(idx: number) {
  const all = Object.values(resultRefs).flat()
  all[idx]?.focus()
}

function handleResultKey(e: KeyboardEvent, item: SearchItem) {
  const all = Object.values(resultRefs).flat()
  const cur = all.indexOf(e.currentTarget as HTMLElement)
  if (e.key === 'ArrowDown') { e.preventDefault(); all[cur + 1]?.focus() }
  if (e.key === 'ArrowUp')   { e.preventDefault(); all[cur - 1]?.focus() ?? inputRef.value?.focus() }
  if (e.key === 'Enter')     { e.preventDefault(); activate(item) }
}

// ── Voz (tarea 17.2) ──────────────────────────────────────────────────────────
const voiceSupported = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)
const listening = ref(false)
const transcript = ref('')
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let recognition: any = null

function startVoice() {
  const SR = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
  recognition = new SR()
  recognition.lang = 'es-ES'
  recognition.interimResults = true
  recognition.continuous = false
  recognition.onresult = (e: any) => {
    const t = Array.from(e.results as SpeechRecognitionResultList)
      .map((r: any) => r[0].transcript)
      .join('')
    transcript.value = t
    query.value = t
    search()
  }
  recognition.onend = () => { listening.value = false; transcript.value = '' }
  recognition.start()
  listening.value = true
}

function stopVoice() {
  recognition?.stop()
  listening.value = false
  transcript.value = ''
}

function toggleVoice() {
  if (listening.value) stopVoice()
  else startVoice()
}

// Ctrl+K global listener (complementa useAccessibility)
function handleKeydown(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    if (open.value) close(); else openSearch()
  }
}
import { onMounted, onUnmounted } from 'vue'
onMounted(() => document.addEventListener('keydown', handleKeydown))
onUnmounted(() => { document.removeEventListener('keydown', handleKeydown); stopVoice() })
</script>

<style scoped>
/* ── Backdrop ── */
.gs-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 10000;
  display: flex; align-items: flex-start; justify-content: center;
  padding: 10vh 1rem 1rem;
}

/* ── Modal ── */
.gs-modal {
  background: var(--surface-card, #fff);
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  width: 100%; max-width: 580px;
  display: flex; flex-direction: column;
  overflow: hidden;
  max-height: 80vh;
}

/* ── Fila de input ── */
.gs-input-row {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--surface-d, #e5e7eb);
}
.gs-search-icon { font-size: 1rem; color: var(--text-color-secondary, #64748b); flex-shrink: 0; }
.gs-input {
  flex: 1; border: none; outline: none; font-size: 1rem;
  background: transparent; color: var(--text-color, #1e293b);
  min-width: 0;
}
.gs-input::placeholder { color: #94a3b8; }
.gs-esc-hint {
  flex-shrink: 0; font-size: 0.68rem; color: #94a3b8;
  border: 1px solid #e2e8f0; border-radius: 0.25rem;
  padding: 0.15rem 0.35rem;
}

/* ── Botón voz ── */
.gs-voice-btn {
  background: none; border: 1px solid var(--surface-d, #e2e8f0); border-radius: 0.5rem;
  padding: 0.3rem 0.5rem; cursor: pointer;
  color: var(--text-color-secondary, #64748b);
  display: flex; align-items: center; gap: 0.25rem; position: relative;
}
.gs-voice-btn--active { color: #ef4444; border-color: #ef4444; }
.gs-voice-btn:focus { outline: 2px solid var(--primary-color, #6366f1); }
.gs-voice-pulse {
  width: 8px; height: 8px; border-radius: 50%; background: #ef4444;
  animation: pulse 1s infinite;
}
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

/* ── Transcripción ── */
.gs-transcript {
  padding: 0.4rem 1rem;
  font-size: 0.8rem; color: #ef4444;
  display: flex; align-items: center; gap: 0.4rem;
  border-bottom: 1px solid var(--surface-d, #e5e7eb);
}

/* ── Resultados ── */
.gs-results { flex: 1; overflow-y: auto; padding: 0.5rem 0; scrollbar-width: thin; }

.gs-hint { padding: 0.5rem 1rem; font-size: 0.78rem; color: var(--text-color-secondary, #64748b); margin: 0; }
.gs-quick { display: flex; flex-wrap: wrap; gap: 0.4rem; padding: 0 1rem 0.75rem; }
.gs-quick-chip {
  border: 1px solid var(--surface-d, #e2e8f0); border-radius: 99px;
  padding: 0.3rem 0.75rem; font-size: 0.78rem; cursor: pointer;
  background: var(--surface-ground, #f8fafc); color: var(--text-color-secondary, #64748b);
  display: flex; align-items: center; gap: 0.3rem;
}
.gs-quick-chip:hover { border-color: var(--primary-color, #6366f1); color: var(--primary-color, #6366f1); }

/* Atajos */
.gs-shortcuts { padding: 0 1rem 0.75rem; }
.gs-shortcuts-title { font-size: 0.72rem; color: #94a3b8; margin: 0 0 0.4rem; }
.gs-shortcut-list { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.gs-shortcut { font-size: 0.72rem; color: var(--text-color-secondary, #64748b); display: flex; align-items: center; gap: 0.25rem; }
kbd {
  border: 1px solid #e2e8f0; border-radius: 0.2rem; padding: 0.1rem 0.3rem;
  font-size: 0.65rem; font-family: inherit; background: var(--surface-ground, #f8fafc);
}

/* Categorías */
.gs-category { margin-bottom: 0.25rem; }
.gs-cat-label {
  padding: 0.3rem 1rem; font-size: 0.68rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: #94a3b8; display: flex; align-items: center; gap: 0.3rem;
}

/* Item de resultado */
.gs-result-item {
  display: flex; align-items: center; gap: 0.75rem;
  width: 100%; padding: 0.6rem 1rem; border: none; background: none; cursor: pointer;
  text-align: left; transition: background 0.1s;
}
.gs-result-item:hover, .gs-result-item:focus { background: var(--surface-ground, #f8fafc); outline: none; }
.gs-result-icon { font-size: 1rem; color: var(--primary-color, #6366f1); flex-shrink: 0; }
.gs-result-text { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.gs-result-title { font-size: 0.85rem; font-weight: 500; color: var(--text-color, #1e293b); }
.gs-result-title :deep(mark) { background: color-mix(in srgb, var(--primary-color, #6366f1) 20%, transparent); border-radius: 2px; }
.gs-result-sub { font-size: 0.72rem; color: var(--text-color-secondary, #64748b); }
.gs-result-badge { font-size: 0.65rem; font-weight: 700; padding: 0.15rem 0.4rem; border-radius: 99px; flex-shrink: 0; }
.badge--pdf   { background: #fee2e2; color: #dc2626; }
.badge--excel { background: #dcfce7; color: #16a34a; }

/* Sin resultados */
.gs-no-results { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; padding: 2rem; color: var(--text-color-secondary, #64748b); text-align: center; }

/* ── Footer ── */
.gs-footer {
  display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;
  padding: 0.5rem 1rem; border-top: 1px solid var(--surface-d, #e5e7eb);
  font-size: 0.68rem; color: #94a3b8;
}
.gs-footer span { display: flex; align-items: center; gap: 0.25rem; }

/* ── Transición ── */
.gs-fade-enter-active, .gs-fade-leave-active { transition: opacity 0.2s ease; }
.gs-fade-enter-from, .gs-fade-leave-to { opacity: 0; }
.gs-fade-enter-active .gs-modal, .gs-fade-leave-active .gs-modal { transition: transform 0.2s ease; }
.gs-fade-enter-from .gs-modal, .gs-fade-leave-to .gs-modal { transform: scale(0.95) translateY(-10px); }

/* ── Accesibilidad ── */
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; }
</style>
